<template>
  <main class="boryx-v21-experience" :class="isPc ? 'is-pc' : 'is-mobile'">
    <div class="boryx-v21-backdrop" aria-hidden="true">
      <span v-for="index in 7" :key="index" :class="'particle particle-' + index"></span>
    </div>

    <section class="boryx-v21-telemetry" aria-label="Platform status">
      <div class="telemetry-brand">
        <span class="telemetry-pulse"></span>
        <strong>{{ text('BORYX 实时系统', 'BORYX LIVE SYSTEM') }}</strong>
      </div>
      <div class="telemetry-track">
        <span><i>01</i>{{ text('实时行情', 'LIVE MARKET DATA') }}</span>
        <span><i>02</i>{{ text('风险监测', 'RISK MONITORING') }}</span>
        <span><i>03</i>{{ text('全球接入', 'GLOBAL ACCESS') }}</span>
        <span><i>04</i>{{ text('全天候支持', '24/7 SUPPORT') }}</span>
      </div>
    </section>

    <section class="boryx-v21-section boryx-v21-market">
      <header class="boryx-v21-heading">
        <div>
          <span class="heading-index">01 / MARKET MATRIX</span>
          <h2>{{ text('实时市场矩阵', 'Live Market Matrix') }}</h2>
          <p>{{ text('连接真实行情数据，快速捕捉全球市场脉动', 'Connected to live data for a clearer view of global market movement') }}</p>
        </div>
        <button type="button" class="heading-action" @click="go('/market')">
          {{ text('查看全部市场', 'View all markets') }}
          <span>↗</span>
        </button>
      </header>

      <div v-if="visibleMarkets.length" class="market-grid">
        <article
          v-for="(item, index) in visibleMarkets"
          :key="item.id || item.code || index"
          class="market-node"
          @click="$emit('select-market', item)"
        >
          <div class="market-node__top">
            <div class="market-identity">
              <div class="market-logo">
                <img v-if="item.logo" :src="item.logo" :alt="marketName(item)" />
                <span v-else>{{ marketInitial(item) }}</span>
              </div>
              <div>
                <strong>{{ marketName(item) }}</strong>
                <small :title="marketLocalizedName(item)">{{ marketLocalizedName(item) }}</small>
              </div>
            </div>
            <span class="market-live"><i></i>LIVE</span>
          </div>

          <div class="market-node__data">
            <div>
              <small>{{ text('最新价格', 'LAST PRICE') }}</small>
              <strong :key="item.liveTick || 'initial'">{{ marketPrice(item) }}</strong>
            </div>
            <span :class="changeClass(item)">{{ marketChange(item) }}</span>
          </div>

          <div class="market-chart-shell">
            <div :id="item.code" class="market-chart"></div>
            <span class="chart-axis axis-left"></span>
            <span class="chart-axis axis-right"></span>
          </div>
        </article>
      </div>

      <div v-else class="market-loading">
        <div class="loading-orbit"><span></span></div>
        <strong>{{ text('正在连接实时行情', 'Connecting to live market data') }}</strong>
        <small>{{ text('数据将从现有行情接口自动载入', 'Data will load automatically from the existing market feed') }}</small>
      </div>
    </section>

    <section class="boryx-v21-section boryx-v21-products">
      <header class="boryx-v21-heading compact">
        <div>
          <span class="heading-index">02 / PRODUCT ENGINES</span>
          <h2>{{ text('三大数字资产引擎', 'Three Digital Asset Engines') }}</h2>
          <p>{{ text('从行情发现到策略执行，构建完整的数字资产体验', 'From market discovery to strategy execution in one connected experience') }}</p>
        </div>
      </header>

      <div class="product-grid">
        <article
          v-for="(item, index) in productModules"
          :key="item.code"
          class="product-engine"
          :class="'engine-' + (index + 1)"
          @click="go(item.path)"
        >
          <div class="engine-topline">
            <span>{{ item.code }}</span>
            <small>{{ item.status }}</small>
          </div>
          <div class="engine-visual" aria-hidden="true">
            <span class="engine-ring ring-a"></span>
            <span class="engine-ring ring-b"></span>
            <span class="engine-core">{{ item.number }}</span>
            <i v-for="dot in 4" :key="dot" :class="'engine-dot dot-' + dot"></i>
          </div>
          <div class="engine-copy">
            <h3>{{ item.title }}</h3>
            <p>{{ item.description }}</p>
            <div class="engine-features">
              <span v-for="feature in item.features" :key="feature">{{ feature }}</span>
            </div>
          </div>
          <button type="button">{{ text('进入系统', 'Open engine') }} <span>→</span></button>
        </article>
      </div>
    </section>

    <section class="boryx-v21-section boryx-v21-security">
      <div class="security-copy">
        <span class="heading-index">03 / SECURITY ARCHITECTURE</span>
        <h2>{{ text('多层安全架构', 'Multi-layer Security Architecture') }}</h2>
        <p>{{ text('以多重防护、实时监测与风险控制，构建覆盖账户和资产的安全体系。', 'Layered protection, real-time monitoring and risk control secure every account and asset.') }}</p>

        <div class="security-metrics">
          <div>
            <strong>24/7</strong>
            <span>{{ text('持续监测', 'Continuous monitoring') }}</span>
          </div>
          <div>
            <strong>99.9%</strong>
            <span>{{ text('系统可用性', 'System availability') }}</span>
          </div>
          <div>
            <strong>4-LAYER</strong>
            <span>{{ text('纵深防护', 'Defense in depth') }}</span>
          </div>
        </div>
      </div>

      <div class="security-orbit" aria-label="Security layers">
        <div class="orbit-ring orbit-ring-a"></div>
        <div class="orbit-ring orbit-ring-b"></div>
        <div class="orbit-ring orbit-ring-c"></div>
        <div class="security-core">
          <img src="@/assets/img/boryx_logo.png" alt="Boryx" />
          <strong>SECURE CORE</strong>
          <small>BORYX EXCHANGE</small>
        </div>
        <div
          v-for="(node, index) in securityNodes"
          :key="node.code"
          class="security-node"
          :class="'security-node-' + (index + 1)"
        >
          <span>{{ node.code }}</span>
          <strong>{{ node.label }}</strong>
        </div>
      </div>
    </section>

    <section class="boryx-v21-section boryx-v21-command">
      <header class="boryx-v21-heading compact">
        <div>
          <span class="heading-index">04 / COMMAND CENTER</span>
          <h2>{{ text('平台服务中心', 'Platform Command Center') }}</h2>
        </div>
      </header>

      <div class="command-grid">
        <article class="command-card command-card--wide" @click="go('/help/notice')">
          <div class="command-code">NOTICE / 01</div>
          <div>
            <h3>{{ $t('home.NewFeaturesInOnline') }}</h3>
            <p>{{ $t('home.NewFeaturesInOnlineInfo') }}</p>
          </div>
          <span class="command-arrow">↗</span>
        </article>
        <article class="command-card" @click="go('/profile')">
          <div class="command-code">ACCOUNT / 02</div>
          <div>
            <h3>{{ text('账户控制台', 'Account Console') }}</h3>
            <p>{{ $t('home.noticeInfo1') }}</p>
          </div>
          <span class="command-arrow">↗</span>
        </article>
        <article class="command-card" @click="go('/market')">
          <div class="command-code">MARKET / 03</div>
          <div>
            <h3>{{ text('探索全球市场', 'Explore Global Markets') }}</h3>
            <p>{{ text('进入实时行情与产品列表', 'Open live quotes and the complete product list') }}</p>
          </div>
          <span class="command-arrow">↗</span>
        </article>
      </div>
    </section>

  </main>
</template>

<script>
export default {
  name: 'BoryxHomeExperience',
  props: {
    isPc: {
      type: Boolean,
      default: true,
    },
    marketList: {
      type: Array,
      default: () => [],
    },
    company: {
      type: Array,
      default: () => [],
    },
    policy: {
      type: Array,
      default: () => [],
    },
    footer: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    isChinese() {
      const locale = this.$i18n && this.$i18n.locale ? this.$i18n.locale : 'zh';
      return String(locale).toLowerCase().indexOf('zh') === 0;
    },
    visibleMarkets() {
      if (!Array.isArray(this.marketList)) return [];
      return this.marketList
        .filter(item => this.isCryptoMarket(item))
        .slice(0, this.isPc ? 6 : 5);
    },
    productModules() {
      return [
        {
          code: 'MARKET ENGINE',
          number: '01',
          status: this.text('实时连接', 'LIVE CONNECTED'),
          title: this.text('数字资产市场', 'Digital Asset Markets'),
          description: this.text('聚合多类数字资产行情，以清晰的数据结构快速发现市场机会。', 'Explore multiple asset classes through a clear, data-rich market interface.'),
          features: this.isChinese ? ['实时行情', '多元产品', '快速检索'] : ['Live Quotes', 'Multi-asset', 'Fast Discovery'],
          path: '/market',
        },
        {
          code: 'STRATEGY ENGINE',
          number: '02',
          status: this.text('策略就绪', 'STRATEGY READY'),
          title: this.text('智能交易引擎', 'Intelligent Trading Engine'),
          description: this.text('连接行情、策略与执行流程，为不同交易需求提供专业工具。', 'Connect market insight, strategy and execution in one professional workflow.'),
          features: this.isChinese ? ['策略执行', '风险控制', '高效连接'] : ['Execution', 'Risk Control', 'Fast Access'],
          path: '/trading',
        },
        {
          code: 'NFT ENGINE',
          number: '03',
          status: this.text('资产在线', 'ASSETS ONLINE'),
          title: this.text('NFT数字资产', 'NFT Digital Assets'),
          description: this.text('探索数字收藏与链上资产，连接更广阔的数字价值网络。', 'Discover digital collectibles and connect to a broader on-chain value network.'),
          features: this.isChinese ? ['数字收藏', '链上资产', '价值发现'] : ['Collectibles', 'On-chain', 'Discovery'],
          path: '/nfts',
        },
      ];
    },
    securityNodes() {
      return [
        { code: 'COLD', label: this.text('冷热钱包', 'Cold Storage') },
        { code: 'M-SIG', label: this.text('多重签名', 'Multi-signature') },
        { code: 'RISK', label: this.text('风险控制', 'Risk Control') },
        { code: 'LIVE', label: this.text('实时监测', 'Live Monitor') },
      ];
    },
  },
  methods: {
    text(zh, en) {
      return this.isChinese ? zh : en;
    },
    go(path) {
      if (this.$route && this.$route.path === path) return;
      this.$router.push(path);
    },
    marketName(item) {
      return item.appName || item.name || item.code || 'Digital Asset';
    },
    normalizeMarketKey(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
        .replace(/(usdt|usdc|usd)$/g, '');
    },
    cryptoMeta(item) {
      const aliases = {
        btc: 'bitcoin',
        xbt: 'bitcoin',
        eth: 'ethereum',
        bnb: 'binancecoin',
        xrp: 'ripple',
        sol: 'solana',
        ada: 'cardano',
        uni: 'uniswap',
        doge: 'dogecoin',
        ltc: 'litecoin',
        dot: 'polkadot',
        avax: 'avalanche',
        link: 'chainlink',
        bch: 'bitcoincash',
        trx: 'tron',
        shib: 'shibainu',
        matic: 'polygon',
      };
      const dictionary = {
        bitcoin: { zh: '比特币', en: 'Bitcoin' },
        ethereum: { zh: '以太坊', en: 'Ethereum' },
        binancecoin: { zh: '币安币', en: 'BNB' },
        ripple: { zh: '瑞波币', en: 'XRP' },
        solana: { zh: '索拉纳', en: 'Solana' },
        cardano: { zh: '艾达币', en: 'Cardano' },
        uniswap: { zh: 'Uniswap', en: 'Uniswap' },
        dogecoin: { zh: '狗狗币', en: 'Dogecoin' },
        litecoin: { zh: '莱特币', en: 'Litecoin' },
        polkadot: { zh: '波卡币', en: 'Polkadot' },
        avalanche: { zh: '雪崩协议', en: 'Avalanche' },
        chainlink: { zh: '链环', en: 'Chainlink' },
        bitcoincash: { zh: '比特币现金', en: 'Bitcoin Cash' },
        tron: { zh: '波场', en: 'TRON' },
        shibainu: { zh: '柴犬币', en: 'Shiba Inu' },
        polygon: { zh: 'Polygon', en: 'Polygon' },
      };
      const candidates = [item.code, item.symbol, item.marketCode, item.appName, item.name];
      for (let index = 0; index < candidates.length; index += 1) {
        const rawKey = this.normalizeMarketKey(candidates[index]);
        const key = aliases[rawKey] || rawKey;
        if (dictionary[key]) return dictionary[key];
      }
      return null;
    },
    isCryptoMarket(item) {
      if (item && item.type !== undefined && item.type !== null && item.type !== '') {
        return String(item.type) === '2';
      }
      return Boolean(this.cryptoMeta(item || {}));
    },
    marketLocalizedName(item) {
      const zh = item.zhName || item.cnName || item.chineseName || item.nameZh || item.appNameZh || item.marketNameZh;
      const en = item.enName || item.englishName || item.nameEn || item.appNameEn || item.marketNameEn;
      const fallback = this.cryptoMeta(item) || {};
      const zhName = zh || fallback.zh;
      const enName = en || fallback.en;
      if (zhName && enName) {
        if (String(zhName).toLowerCase() === String(enName).toLowerCase()) return zhName;
        return this.isChinese ? `${zhName} / ${enName}` : `${enName} / ${zhName}`;
      }
      return zhName || enName || this.text('加密货币', 'Cryptocurrency');
    },
    marketInitial(item) {
      const name = this.marketName(item);
      return String(name).slice(0, 1).toUpperCase();
    },
    marketPrice(item) {
      const value = item.nowPrice !== undefined && item.nowPrice !== null ? item.nowPrice : item.price;
      return value !== undefined && value !== null && value !== '' ? value : '--';
    },
    marketChange(item) {
      const value = Number(item.zdf);
      if (!Number.isFinite(value)) return '--';
      return (value > 0 ? '+' : '') + value.toFixed(2) + '%';
    },
    changeClass(item) {
      const value = Number(item.zdf);
      if (!Number.isFinite(value)) return 'is-flat';
      return value >= 0 ? 'is-up' : 'is-down';
    },
  },
};
</script>

<style scoped>
.boryx-v21-experience {
  position: relative;
  width: 12rem;
  margin: 0.34rem auto 0;
  padding-bottom: 0.68rem;
  color: #f5f6f8;
  font-family: "Mabry Pro", "Microsoft YaHei", Arial, sans-serif;
}

.boryx-v21-backdrop {
  position: absolute;
  inset: -0.7rem -1.2rem 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(232, 201, 138, 0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(232, 201, 138, 0.035) 1px, transparent 1px);
  background-size: 0.52rem 0.52rem;
  mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.9), transparent 94%);
}

.boryx-v21-backdrop::before,
.boryx-v21-backdrop::after {
  content: "";
  position: absolute;
  border-radius: 50%;
  filter: blur(0.1rem);
}

.boryx-v21-backdrop::before {
  width: 5.4rem;
  height: 5.4rem;
  top: 1.2rem;
  right: -1.4rem;
  border: 1px solid rgba(232, 201, 138, 0.11);
  box-shadow: 0 0 1.2rem rgba(201, 154, 77, 0.08), inset 0 0 0.8rem rgba(201, 154, 77, 0.035);
}

.boryx-v21-backdrop::after {
  width: 4.2rem;
  height: 4.2rem;
  left: -1.3rem;
  top: 8.4rem;
  border: 1px solid rgba(217, 221, 227, 0.07);
  box-shadow: 0 0 0.9rem rgba(217, 221, 227, 0.04);
}

.particle {
  position: absolute;
  width: 0.04rem;
  height: 0.04rem;
  border-radius: 50%;
  background: #e8c98a;
  box-shadow: 0 0 0.16rem rgba(242, 184, 91, 0.78);
  animation: particle-float 9s ease-in-out infinite;
}

.particle-1 { left: 8%; top: 6%; }
.particle-2 { left: 31%; top: 19%; animation-delay: -2s; }
.particle-3 { left: 67%; top: 9%; animation-delay: -4s; }
.particle-4 { right: 8%; top: 27%; animation-delay: -6s; }
.particle-5 { left: 15%; top: 52%; animation-delay: -3s; }
.particle-6 { right: 24%; top: 68%; animation-delay: -5s; }
.particle-7 { left: 48%; top: 86%; animation-delay: -7s; }

.boryx-v21-telemetry,
.boryx-v21-section {
  position: relative;
  z-index: 1;
}

.boryx-v21-telemetry {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 0.64rem;
  padding: 0 0.22rem;
  overflow: hidden;
  border: 1px solid rgba(232, 201, 138, 0.2);
  border-radius: 0.14rem;
  background: linear-gradient(90deg, rgba(201, 154, 77, 0.12), rgba(8, 12, 18, 0.9) 34%, rgba(217, 221, 227, 0.035));
  box-shadow: inset 0 1px rgba(255, 255, 255, 0.035), 0 0.16rem 0.4rem rgba(0, 0, 0, 0.22);
}

.boryx-v21-telemetry::after {
  content: "";
  position: absolute;
  top: 0;
  left: -20%;
  width: 18%;
  height: 1px;
  background: linear-gradient(90deg, transparent, #f0d59d, transparent);
  box-shadow: 0 0 0.12rem rgba(242, 184, 91, 0.72);
  animation: telemetry-scan 5.4s linear infinite;
}

.telemetry-brand,
.telemetry-track,
.telemetry-track span {
  display: flex;
  align-items: center;
}

.telemetry-brand {
  flex: 0 0 auto;
  gap: 0.1rem;
  color: #f0d59d;
  font-size: 0.14rem;
  letter-spacing: 0.08em;
}

.telemetry-pulse {
  width: 0.08rem;
  height: 0.08rem;
  border-radius: 50%;
  background: #e8c98a;
  box-shadow: 0 0 0 0 rgba(232, 201, 138, 0.5);
  animation: live-pulse 2.1s ease-out infinite;
}

.telemetry-track {
  min-width: 0;
  gap: 0.28rem;
  color: #8e97a4;
  font-size: 0.12rem;
  letter-spacing: 0.05em;
}

.telemetry-track span {
  gap: 0.07rem;
  white-space: nowrap;
}

.telemetry-track i {
  color: #c89542;
  font-style: normal;
  font-family: monospace;
}

.boryx-v21-section {
  margin-top: 0.34rem;
  padding: 0.42rem;
  overflow: hidden;
  border: 1px solid rgba(232, 201, 138, 0.18);
  border-radius: 0.24rem;
  background:
    radial-gradient(circle at 92% 5%, rgba(201, 154, 77, 0.13), transparent 24%),
    linear-gradient(145deg, rgba(14, 20, 29, 0.96), rgba(5, 8, 12, 0.98) 68%);
  box-shadow: 0 0.28rem 0.72rem rgba(0, 0, 0, 0.3), inset 0 1px rgba(255, 255, 255, 0.03);
}

.boryx-v21-section::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(232, 201, 138, 0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(232, 201, 138, 0.035) 1px, transparent 1px);
  background-size: 0.4rem 0.4rem;
  mask-image: linear-gradient(110deg, rgba(0, 0, 0, 0.52), transparent 68%);
}

.boryx-v21-heading {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 0.3rem;
  margin-bottom: 0.3rem;
}

.boryx-v21-heading.compact {
  margin-bottom: 0.34rem;
}

.heading-index {
  display: block;
  margin-bottom: 0.09rem;
  color: #c89542;
  font-family: monospace;
  font-size: 0.13rem;
  letter-spacing: 0.16em;
}

.boryx-v21-heading h2,
.security-copy h2 {
  margin: 0;
  color: #f5f6f8;
  font-size: 0.4rem;
  line-height: 1.2;
  letter-spacing: -0.02em;
}

.boryx-v21-heading p,
.security-copy > p {
  max-width: 6.6rem;
  margin: 0.1rem 0 0;
  color: #8e97a4;
  font-size: 0.17rem;
  line-height: 1.7;
}

.heading-action {
  flex: 0 0 auto;
  height: 0.4rem;
  padding: 0 0.18rem;
  border: 1px solid rgba(232, 201, 138, 0.42);
  border-radius: 0.12rem;
  color: #e8c98a;
  background: rgba(201, 154, 77, 0.08);
  cursor: pointer;
  font-size: 0.14rem;
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease;
}

.heading-action:hover {
  transform: translateY(-2px);
  border-color: #e8c98a;
  background: rgba(201, 154, 77, 0.16);
}

.heading-action span {
  margin-left: 0.08rem;
}

.market-grid {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.16rem;
}

.market-node {
  position: relative;
  min-width: 0;
  padding: 0.2rem;
  overflow: hidden;
  border: 1px solid rgba(217, 221, 227, 0.11);
  border-radius: 0.16rem;
  background: linear-gradient(145deg, rgba(15, 22, 31, 0.92), rgba(5, 8, 12, 0.94));
  box-shadow: inset 0 1px rgba(255, 255, 255, 0.025);
  cursor: pointer;
  transition: transform 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease;
}

.market-node::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  width: 0.42rem;
  height: 1px;
  background: linear-gradient(90deg, #c89542, transparent);
}

.market-node:hover {
  transform: translateY(-3px);
  border-color: rgba(232, 201, 138, 0.42);
  box-shadow: 0 0.18rem 0.42rem rgba(0, 0, 0, 0.32), 0 0 0.22rem rgba(201, 154, 77, 0.07);
}

.market-node__top,
.market-identity,
.market-node__data {
  display: flex;
  align-items: center;
}

.market-node__top,
.market-node__data {
  justify-content: space-between;
  gap: 0.12rem;
}

.market-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 0.4rem;
  width: 0.4rem;
  height: 0.4rem;
  margin-right: 0.1rem;
  overflow: hidden;
  border: 1px solid rgba(232, 201, 138, 0.26);
  border-radius: 50%;
  color: #090b0e;
  background: linear-gradient(135deg, #f0d59d, #c89542);
  font-size: 0.17rem;
  font-weight: 800;
}

.market-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.market-identity strong,
.market-identity small {
  display: block;
}

.market-identity strong {
  max-width: 1.65rem;
  overflow: hidden;
  color: #f1f3f5;
  font-size: 0.17rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.market-identity small {
  max-width: 1.9rem;
  margin-top: 0.04rem;
  color: #727d8b;
  font-family: monospace;
  font-size: 0.12rem;
  line-height: 1.35;
  white-space: normal;
}

.market-live {
  display: inline-flex;
  align-items: center;
  gap: 0.05rem;
  color: #b0b7c1;
  font-family: monospace;
  font-size: 0.11rem;
}

.market-live i {
  width: 0.05rem;
  height: 0.05rem;
  border-radius: 50%;
  background: #e8c98a;
  box-shadow: 0 0 0.08rem rgba(242, 184, 91, 0.76);
}

.market-node__data {
  margin-top: 0.22rem;
}

.market-node__data small,
.market-node__data strong {
  display: block;
}

.market-node__data small {
  color: #697482;
  font-size: 0.12rem;
  letter-spacing: 0.08em;
}

.market-node__data strong {
  max-width: 2rem;
  margin-top: 0.05rem;
  overflow: hidden;
  color: #f4f5f7;
  font-family: monospace;
  font-size: 0.24rem;
  text-overflow: ellipsis;
  animation: market-price-pulse 0.48s ease-out;
}

.market-node__data > span {
  padding: 0.05rem 0.08rem;
  border-radius: 0.08rem;
  font-family: monospace;
  font-size: 0.14rem;
}

.market-node__data .is-up {
  color: #72d49b;
  background: rgba(54, 178, 112, 0.1);
}

.market-node__data .is-down {
  color: #ff8585;
  background: rgba(255, 88, 88, 0.1);
}

.market-node__data .is-flat {
  color: #aab1bb;
  background: rgba(217, 221, 227, 0.06);
}

.market-chart-shell {
  position: relative;
  height: 0.78rem;
  margin-top: 0.12rem;
  overflow: hidden;
  border-top: 1px solid rgba(217, 221, 227, 0.06);
  background-image: linear-gradient(rgba(217, 221, 227, 0.035) 1px, transparent 1px);
  background-size: 100% 0.24rem;
}

.market-chart {
  position: relative;
  z-index: 2;
  width: 100%;
  height: 100%;
}

.chart-axis {
  position: absolute;
  bottom: 0.08rem;
  width: 0.04rem;
  height: 0.04rem;
  border-bottom: 1px solid rgba(232, 201, 138, 0.44);
}

.axis-left { left: 0; border-left: 1px solid rgba(232, 201, 138, 0.44); }
.axis-right { right: 0; border-right: 1px solid rgba(232, 201, 138, 0.44); }

.market-loading {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 2.3rem;
  border: 1px dashed rgba(232, 201, 138, 0.2);
  border-radius: 0.16rem;
  color: #d9dde3;
  text-align: center;
}

.market-loading small {
  margin-top: 0.08rem;
  color: #77818e;
  font-size: 0.13rem;
}

.loading-orbit {
  position: relative;
  width: 0.52rem;
  height: 0.52rem;
  margin-bottom: 0.16rem;
  border: 1px solid rgba(232, 201, 138, 0.24);
  border-radius: 50%;
  animation: orbit-spin 2.6s linear infinite;
}

.loading-orbit span {
  position: absolute;
  top: -0.04rem;
  left: 50%;
  width: 0.08rem;
  height: 0.08rem;
  border-radius: 50%;
  background: #e8c98a;
  box-shadow: 0 0 0.14rem rgba(242, 184, 91, 0.7);
}

.product-grid {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.18rem;
}

.product-engine {
  position: relative;
  min-height: 4.35rem;
  padding: 0.24rem;
  overflow: hidden;
  border: 1px solid rgba(232, 201, 138, 0.18);
  border-radius: 0.2rem;
  background: linear-gradient(155deg, rgba(16, 23, 33, 0.96), rgba(5, 8, 12, 0.98));
  cursor: pointer;
  transition: transform 0.24s ease, border-color 0.24s ease, box-shadow 0.24s ease;
}

.product-engine::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(circle at 50% 35%, rgba(201, 154, 77, 0.14), transparent 31%);
}

.product-engine:hover {
  transform: translateY(-0.06rem);
  border-color: rgba(232, 201, 138, 0.52);
  box-shadow: 0 0.28rem 0.62rem rgba(0, 0, 0, 0.38), 0 0 0.28rem rgba(201, 154, 77, 0.09);
}

.engine-topline {
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  gap: 0.12rem;
  color: #c89542;
  font-family: monospace;
  font-size: 0.12rem;
  letter-spacing: 0.08em;
}

.engine-topline small {
  color: #788391;
  font-size: 0.11rem;
}

.engine-visual {
  position: relative;
  z-index: 1;
  width: 1.5rem;
  height: 1.5rem;
  margin: 0.28rem auto 0.2rem;
}

.engine-ring,
.engine-core,
.engine-dot {
  position: absolute;
  border-radius: 50%;
}

.engine-ring {
  inset: 0;
  border: 1px solid rgba(232, 201, 138, 0.22);
}

.ring-a {
  border-top-color: #e8c98a;
  border-bottom-color: rgba(217, 221, 227, 0.42);
  animation: orbit-spin 9s linear infinite;
}

.ring-b {
  inset: 0.22rem;
  border-left-color: #c89542;
  border-right-color: rgba(217, 221, 227, 0.3);
  animation: orbit-spin-reverse 6.5s linear infinite;
}

.engine-core {
  inset: 0.46rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #090b0e;
  background: linear-gradient(135deg, #f0d59d, #c89542 62%, #e8c98a);
  box-shadow: 0 0 0.34rem rgba(201, 154, 77, 0.3);
  font-family: monospace;
  font-size: 0.16rem;
  font-weight: 800;
}

.engine-dot {
  width: 0.06rem;
  height: 0.06rem;
  background: #e8c98a;
  box-shadow: 0 0 0.1rem rgba(242, 184, 91, 0.68);
}

.dot-1 { left: 0.1rem; top: 0.3rem; }
.dot-2 { right: 0.02rem; top: 0.65rem; }
.dot-3 { left: 0.48rem; bottom: 0.02rem; }
.dot-4 { right: 0.34rem; top: 0.02rem; }

.engine-copy {
  position: relative;
  z-index: 2;
}

.engine-copy h3 {
  margin: 0;
  color: #f3f4f6;
  font-size: 0.24rem;
  text-align: center;
}

.engine-copy p {
  min-height: 0.74rem;
  margin: 0.12rem 0 0;
  color: #89939f;
  font-size: 0.15rem;
  line-height: 1.65;
  text-align: center;
}

.engine-features {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.06rem;
  margin-top: 0.14rem;
}

.engine-features span {
  padding: 0.04rem 0.07rem;
  border: 1px solid rgba(217, 221, 227, 0.1);
  border-radius: 999px;
  color: #a3abb5;
  background: rgba(255, 255, 255, 0.025);
  font-size: 0.12rem;
}

.product-engine > button {
  position: relative;
  z-index: 3;
  right: auto;
  bottom: auto;
  left: auto;
  width: 100%;
  height: 0.42rem;
  margin-top: 0.2rem;
  box-sizing: border-box;
  border: 1px solid rgba(232, 201, 138, 0.35);
  border-radius: 0.1rem;
  color: #e8c98a;
  background: rgba(201, 154, 77, 0.07);
  font-size: 0.14rem;
  cursor: pointer;
}

.product-engine:hover > button {
  color: #090b0e;
  background: linear-gradient(135deg, #f0d59d, #c89542);
}

.product-engine > button span {
  margin-left: 0.08rem;
}

.boryx-v21-security {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 5.2rem;
  align-items: center;
  gap: 0.5rem;
  min-height: 5.2rem;
}

.security-copy {
  position: relative;
  z-index: 2;
  padding-left: 0.16rem;
}

.security-copy h2 {
  font-size: 0.44rem;
}

.security-copy > p {
  max-width: 5.1rem;
  margin-top: 0.18rem;
}

.security-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.1rem;
  margin-top: 0.32rem;
}

.security-metrics div {
  padding: 0.16rem;
  border: 1px solid rgba(232, 201, 138, 0.14);
  border-radius: 0.12rem;
  background: rgba(255, 255, 255, 0.02);
}

.security-metrics strong,
.security-metrics span {
  display: block;
}

.security-metrics strong {
  color: #f0d59d;
  font-family: monospace;
  font-size: 0.22rem;
}

.security-metrics span {
  margin-top: 0.06rem;
  color: #7f8996;
  font-size: 0.12rem;
}

.security-orbit {
  position: relative;
  z-index: 2;
  width: 4.5rem;
  height: 4.5rem;
  margin: 0 auto;
}

.orbit-ring {
  position: absolute;
  border: 1px solid rgba(232, 201, 138, 0.18);
  border-radius: 50%;
}

.orbit-ring::after {
  content: "";
  position: absolute;
  top: -0.04rem;
  left: 50%;
  width: 0.08rem;
  height: 0.08rem;
  border-radius: 50%;
  background: #e8c98a;
  box-shadow: 0 0 0.16rem rgba(242, 184, 91, 0.74);
}

.orbit-ring-a {
  inset: 0.18rem;
  border-top-color: rgba(240, 213, 157, 0.72);
  animation: orbit-spin 18s linear infinite;
}

.orbit-ring-b {
  inset: 0.72rem;
  border-right-color: rgba(217, 221, 227, 0.52);
  animation: orbit-spin-reverse 13s linear infinite;
}

.orbit-ring-c {
  inset: 1.24rem;
  border-bottom-color: rgba(201, 154, 77, 0.68);
  animation: orbit-spin 9s linear infinite;
}

.security-core {
  position: absolute;
  inset: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(232, 201, 138, 0.36);
  border-radius: 50%;
  background: radial-gradient(circle, rgba(201, 154, 77, 0.24), rgba(5, 8, 12, 0.96) 68%);
  box-shadow: 0 0 0.6rem rgba(201, 154, 77, 0.16), inset 0 0 0.4rem rgba(201, 154, 77, 0.09);
}

.security-core img {
  width: 0.62rem;
  height: 0.62rem;
  object-fit: contain;
}

.security-core strong {
  margin-top: 0.07rem;
  color: #f0d59d;
  font-family: monospace;
  font-size: 0.12rem;
}

.security-core small {
  margin-top: 0.04rem;
  color: #737e8b;
  font-size: 0.09rem;
  letter-spacing: 0.08em;
}

.security-node {
  position: absolute;
  width: 1.2rem;
  padding: 0.1rem;
  border: 1px solid rgba(232, 201, 138, 0.3);
  border-radius: 0.11rem;
  background: rgba(7, 11, 16, 0.94);
  box-shadow: 0 0.1rem 0.24rem rgba(0, 0, 0, 0.35);
  text-align: center;
}

.security-node::after {
  content: "";
  position: absolute;
  width: 0.42rem;
  height: 1px;
  background: linear-gradient(90deg, rgba(232, 201, 138, 0.5), transparent);
}

.security-node span,
.security-node strong {
  display: block;
}

.security-node span {
  color: #c89542;
  font-family: monospace;
  font-size: 0.11rem;
}

.security-node strong {
  margin-top: 0.04rem;
  color: #d9dde3;
  font-size: 0.13rem;
}

.security-node-1 { top: 0.18rem; left: 50%; transform: translateX(-50%); }
.security-node-1::after { left: 50%; bottom: -0.25rem; width: 1px; height: 0.25rem; background: linear-gradient(rgba(232, 201, 138, 0.5), transparent); }
.security-node-2 { top: 50%; right: -0.02rem; transform: translateY(-50%); }
.security-node-2::after { left: -0.32rem; top: 50%; }
.security-node-3 { bottom: 0.18rem; left: 50%; transform: translateX(-50%); }
.security-node-3::after { left: 50%; top: -0.25rem; width: 1px; height: 0.25rem; background: linear-gradient(transparent, rgba(232, 201, 138, 0.5)); }
.security-node-4 { top: 50%; left: -0.02rem; transform: translateY(-50%); }
.security-node-4::after { right: -0.32rem; top: 50%; transform: rotate(180deg); }

.command-grid {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.16rem;
}

.command-card {
  position: relative;
  min-height: 1.48rem;
  padding: 0.22rem;
  overflow: hidden;
  border: 1px solid rgba(217, 221, 227, 0.12);
  border-radius: 0.16rem;
  background: linear-gradient(135deg, rgba(16, 23, 33, 0.94), rgba(6, 9, 14, 0.97));
  cursor: pointer;
  transition: transform 0.22s ease, border-color 0.22s ease, background 0.22s ease;
}

.command-card--wide {
  grid-row: span 2;
  min-height: 3.12rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  background:
    radial-gradient(circle at 82% 18%, rgba(201, 154, 77, 0.2), transparent 31%),
    linear-gradient(145deg, rgba(16, 23, 33, 0.96), rgba(5, 8, 12, 0.98));
}

.command-card::before {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  width: 1.6rem;
  height: 1.6rem;
  border-top: 1px solid rgba(232, 201, 138, 0.15);
  border-right: 1px solid rgba(232, 201, 138, 0.15);
  transform: translate(0.55rem, -0.55rem) rotate(45deg);
}

.command-card:hover {
  transform: translateY(-3px);
  border-color: rgba(232, 201, 138, 0.46);
  background-color: rgba(201, 154, 77, 0.08);
}

.command-code {
  position: relative;
  z-index: 2;
  color: #c89542;
  font-family: monospace;
  font-size: 0.12rem;
  letter-spacing: 0.12em;
}

.command-card h3 {
  position: relative;
  z-index: 2;
  margin: 0.18rem 0 0;
  color: #f1f3f5;
  font-size: 0.24rem;
}

.command-card p {
  position: relative;
  z-index: 2;
  max-width: 4.5rem;
  margin: 0.1rem 0 0;
  color: #86909c;
  font-size: 0.15rem;
  line-height: 1.65;
}

.command-arrow {
  position: absolute;
  right: 0.2rem;
  bottom: 0.18rem;
  color: #e8c98a;
  font-size: 0.26rem;
}

@keyframes telemetry-scan {
  from { left: -20%; }
  to { left: 120%; }
}

@keyframes market-price-pulse {
  0% { color: #f0d59d; text-shadow: 0 0 0.14rem rgba(232, 201, 138, 0.78); }
  100% { color: #f4f5f7; text-shadow: none; }
}

@keyframes live-pulse {
  0% { box-shadow: 0 0 0 0 rgba(232, 201, 138, 0.48); }
  70% { box-shadow: 0 0 0 0.09rem rgba(232, 201, 138, 0); }
  100% { box-shadow: 0 0 0 0 rgba(232, 201, 138, 0); }
}

@keyframes particle-float {
  0%, 100% { transform: translate3d(0, 0, 0); opacity: 0.34; }
  50% { transform: translate3d(0.18rem, -0.28rem, 0); opacity: 0.9; }
}

@keyframes orbit-spin {
  to { transform: rotate(360deg); }
}

@keyframes orbit-spin-reverse {
  to { transform: rotate(-360deg); }
}

@media (max-width: 1000px) {
  .boryx-v21-experience {
    width: 100%;
    margin-top: 0.12rem;
    padding: 0 0.24rem calc(1.16rem + env(safe-area-inset-bottom));
    box-sizing: border-box;
  }

  .boryx-v21-backdrop {
    inset: -0.4rem 0 0;
  }

  .boryx-v21-telemetry {
    display: block;
    min-height: 0;
    padding: 0.14rem;
  }

  .telemetry-brand {
    margin-bottom: 0.12rem;
    font-size: 0.14rem;
  }

  .telemetry-track {
    width: 100%;
    padding-bottom: 0.04rem;
    overflow-x: auto;
    gap: 0.2rem;
    font-size: 0.12rem;
    scrollbar-width: none;
  }

  .telemetry-track::-webkit-scrollbar {
    display: none;
  }

  .boryx-v21-section {
    margin-top: 0.18rem;
    padding: 0.24rem 0.18rem;
    border-radius: 0.18rem;
  }

  .boryx-v21-heading {
    display: block;
    margin-bottom: 0.2rem;
  }

  .heading-index {
    font-size: 0.11rem;
  }

  .boryx-v21-heading h2,
  .security-copy h2 {
    font-size: 0.3rem;
  }

  .boryx-v21-heading p,
  .security-copy > p {
    font-size: 0.15rem;
  }

  .heading-action {
    width: 100%;
    margin-top: 0.14rem;
  }

  .market-grid {
    display: flex;
    margin-right: -0.16rem;
    padding-right: 0.16rem;
    padding-bottom: 0.08rem;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
  }

  .market-grid::-webkit-scrollbar {
    display: none;
  }

  .market-node {
    flex: 0 0 3rem;
    scroll-snap-align: start;
  }

  .product-grid {
    grid-template-columns: 1fr;
  }

  .product-engine {
    min-height: 4.2rem;
  }

  .engine-copy p {
    min-height: 0;
  }

  .product-engine > button {
    margin-top: 0.24rem;
  }

  .boryx-v21-security {
    display: block;
    min-height: 0;
  }

  .security-copy {
    padding: 0;
  }

  .security-metrics {
    gap: 0.06rem;
  }

  .security-metrics div {
    padding: 0.11rem 0.08rem;
  }

  .security-metrics strong {
    font-size: 0.18rem;
  }

  .security-orbit {
    width: 3.35rem;
    height: 3.35rem;
    margin-top: 0.24rem;
  }

  .orbit-ring-a { inset: 0.12rem; }
  .orbit-ring-b { inset: 0.54rem; }
  .orbit-ring-c { inset: 0.95rem; }
  .security-core { inset: 1.12rem; }

  .security-core img {
    width: 0.45rem;
    height: 0.45rem;
  }

  .security-core strong {
    font-size: 0.1rem;
  }

  .security-core small {
    display: none;
  }

  .security-node {
    width: 0.92rem;
    padding: 0.07rem 0.04rem;
  }

  .security-node strong {
    font-size: 0.11rem;
  }

  .security-node-1,
  .security-node-3 {
    left: 50%;
  }

  .security-node-2 { right: -0.03rem; }
  .security-node-4 { left: -0.03rem; }

  .command-grid {
    grid-template-columns: 1fr;
  }

  .command-card,
  .command-card--wide {
    grid-row: auto;
    min-height: 1.48rem;
  }

}

@media (prefers-reduced-motion: reduce) {
  .particle,
  .boryx-v21-telemetry::after,
  .telemetry-pulse,
  .loading-orbit,
  .market-node__data strong,
  .engine-ring,
  .orbit-ring {
    animation: none !important;
  }
}
</style>
